import React, { useState } from "react";
import { experienceData } from "../data/mock";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { FolderOpen, ChevronDown, ChevronUp } from "lucide-react";

const ProjectItem = ({ project, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
        expanded
          ? "bg-teal-50/30 border-teal-100/50"
          : "bg-slate-50/70 border-slate-100/80 hover:bg-teal-50/20 hover:border-teal-100/40"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      <Badge className="bg-teal-100/60 text-teal-700 border-0 text-[10px] font-semibold mt-0.5 shrink-0 hover:bg-teal-100/60">
        {String(index + 1).padStart(2, "0")}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-slate-800">
            {project.name}
          </p>
          <button className="text-slate-400 hover:text-slate-600 transition-colors shrink-0">
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        {expanded && (
          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed animate-in fade-in slide-in-from-top-1 duration-200">
            {project.description}
          </p>
        )}
      </div>
    </div>
  );
};

const ProjectCard = ({ role }) => {
  return (
    <Card className="border border-slate-100 bg-white hover:border-teal-100 hover:shadow-md transition-all duration-300">
      <CardContent className="p-6">
        {/* Role header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600 shrink-0">
            <FolderOpen size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {role.role}
            </h3>
            <p className="text-xs text-slate-400">{role.period}</p>
          </div>
        </div>

        {/* Project list - each individually expandable */}
        <div className="space-y-2.5">
          {role.projects.map((project, i) => (
            <ProjectItem key={i} project={project} index={i} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const Projects = () => {
  const rolesWithProjects = experienceData.filter(
    (role) => role.projects && role.projects.length > 0
  );

  if (rolesWithProjects.length === 0) return null;

  return (
    <section id="projects" className="py-14 lg:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-12 lg:mb-16">
          <p className="text-sm font-medium text-teal-600 tracking-wide uppercase mb-2">
            Initiatives & Impact
          </p>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
            Key Projects
          </h2>
          <p className="mt-3 text-slate-500 leading-relaxed">
            Notable projects and initiatives led across different roles at J.B. Hunt.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {rolesWithProjects.map((role) => (
            <ProjectCard key={role.id} role={role} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
